import { FC, useEffect, useMemo, useState } from "react";
import { Box, Button, Drawer, IconButton, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import { GridColDef, GridValueGetterParams } from "@mui/x-data-grid";
import CustomDataGrid from "../DataGrid/DataGrid";
import InventoryEmptyState from "./InventoryEmptyState";
import SkuVariantForm from "./SkuVariantForm";
import ConfirmDialog from "../Dialog/ConfirmDialog";
import { useUnitName } from "./pickers/useUnitName";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchSkuVariants, deleteSkuVariant } from "../../store/skuVariantStore/skuVariantThunk";
import { ApiStatus, defaultRowsPerPageOptions } from "../../utils/api.types";

const SkuVariantsList: FC = () => {
  const dispatch = useAppDispatch();
  const { variants, status } = useAppSelector((state) => state.skuVariants);
  const unitName = useUnitName();

  // Right-side drawer form (new / edit), same pattern as Locations.
  const [openDrawer, setOpenDrawer] = useState(false);
  const [editVariantId, setEditVariantId] = useState<string | undefined>(undefined);

  // Delete confirmation.
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);

  const loadData = () => dispatch(fetchSkuVariants());

  useEffect(() => {
    loadData();
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => {
    setEditVariantId(undefined);
    setOpenDrawer(true);
  };
  const openEdit = (id: string) => {
    setEditVariantId(id);
    setOpenDrawer(true);
  };
  const closeDrawer = (refresh: boolean) => {
    setOpenDrawer(false);
    setEditVariantId(undefined);
    if (refresh) loadData();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await dispatch(deleteSkuVariant(deleteTarget._id)).unwrap();
      setDeleteTarget(null);
      loadData();
    } catch {
      /* toast handled in slice */
    } finally {
      setDeleting(false);
    }
  };

  // "Chota Peg (30 ml), Peg (60 ml), Patiala (90 ml)"
  const variantsSummary = (list: any[]) =>
    (list || [])
      .map((v: any) => `${v.name} (${v.qty} ${v.unit?.name || unitName(v.unit)})`)
      .join(", ");

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "name",
        headerName: "Variant Set",
        flex: 1,
        minWidth: 160,
        renderCell: ({ row }: GridValueGetterParams) => (
          <span style={{ color: "#488dff", cursor: "pointer" }} onClick={() => openEdit(row._id)}>
            {row.name}
          </span>
        ),
      },
      {
        field: "variants",
        headerName: "Variants",
        flex: 2,
        minWidth: 260,
        sortable: false,
        valueGetter: (p: GridValueGetterParams) => variantsSummary(p.row.variants),
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0.6,
        minWidth: 120,
        sortable: false,
        renderCell: ({ row }: GridValueGetterParams) => (
          <>
            <IconButton
              style={{ color: "#49a563", backgroundColor: "#edf6ef", border: "0.1px solid #49a563", height: 35, width: 35, marginRight: 10 }}
              size="small"
              onClick={() => openEdit(row._id)}
            >
              <EditIcon style={{ fontSize: 16 }} />
            </IconButton>
            <Tooltip title="Delete Variant Set">
              <IconButton
                style={{ color: "#d32f2f", backgroundColor: "#fff", border: "0.1px solid #d32f2f", height: 35, width: 35 }}
                size="small"
                onClick={() => setDeleteTarget(row)}
              >
                <DeleteIcon style={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </>
        ),
      },
    ],
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", py: 1 }}>
        <Button startIcon={<AddIcon />} size="small" variant="contained" onClick={openNew}>
          New Variant Set
        </Button>
      </Box>

      {status !== ApiStatus.LOADING && (variants?.length || 0) === 0 ? (
        <InventoryEmptyState
          height="75vh"
          icon={<CategoryOutlinedIcon />}
          title="No Variant Sets"
          description="Group loose-sale portions into a reusable set — e.g. a 'Whisky Pegs' set with 30ml, 60ml and 90ml rows — then reuse it across every bottle. Create your first set to get started."
          actionLabel="New Variant Set"
          onAction={openNew}
          caption="Need help? Check the Guide tab on inventory management"
        />
      ) : (
        <div style={{ height: "75vh", width: "100%" }}>
          <CustomDataGrid
            columns={columns}
            rows={variants}
            rowCount={variants.length}
            rowsPerPageOptions={defaultRowsPerPageOptions}
            getRowId={(row: any) => row._id || Math.random().toString()}
            loading={status === ApiStatus.LOADING}
          />
        </div>
      )}

      {/* Variant Set Form Drawer (new / edit) */}
      <Drawer disableEscapeKeyDown={false} anchor="right" open={openDrawer} onClose={() => closeDrawer(false)}>
        <SkuVariantForm open={openDrawer} onClose={closeDrawer} variantId={editVariantId} />
      </Drawer>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        loading={deleting}
        title="Delete Variant Set"
        message={<>Are you sure you want to delete the variant set <b>{deleteTarget?.name}</b>?</>}
        warningText="This will permanently remove the set and all its variant rows. This data cannot be recovered once deleted."
        confirmLabel="Delete Variant Set"
      />
    </Box>
  );
};

export default SkuVariantsList;
